import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { useFetchNextScheduledAppointmentForType } from '@/hooks/useScheduleExpectations/useScheduleExpectations';
import PersonContext from '@/pages/Person/PersonContext';
import { ScheduleExpectationCycle } from '@/types/scheduleExpectation';
import Alert from '@/ui/components/Alert';
import { daysUntilMin0, formatMonthDayOrToday } from '@/utils/dates';

const BLUEPRINT_KEY = 'med_provider_appointment_follow-up';

interface CycleEntryWithData {
  label: string;
  cycle: ScheduleExpectationCycle;
}

interface Props {
  selectedBlueprintKey: string | undefined;
}

const NextScheduledAppointmentAlert = ({ selectedBlueprintKey }: Props) => {
  const { config } = useContext(AppContext);
  const personContext = useContext(PersonContext);

  const episodeOfCare = personContext?.person?.episode_of_care;
  const phaseId = episodeOfCare?.current_active_phase?.id ?? '';
  const isMedProviderFollowUp = selectedBlueprintKey === BLUEPRINT_KEY;
  const shouldFetchCycles = isMedProviderFollowUp && !!phaseId;

  const { data: psychiatryCycle } = useFetchNextScheduledAppointmentForType({
    phaseId,
    appointmentTypeHealthieId: config.PSYCHIATRY_CHECKIN_APPT_TYPE_ID,
    enabled: shouldFetchCycles
  });

  const { data: medicalCycle } = useFetchNextScheduledAppointmentForType({
    phaseId,
    appointmentTypeHealthieId: config.MEDICAL_CHECKIN_APPT_TYPE_ID,
    enabled: shouldFetchCycles
  });

  const { data: dualDiagnosisCycle } = useFetchNextScheduledAppointmentForType({
    phaseId,
    appointmentTypeHealthieId: config.DUAL_DIAGNOSIS_PSYCHIATRY_FOLLOWUP_APPT_TYPE_ID,
    enabled: shouldFetchCycles
  });

  if (!isMedProviderFollowUp) return null;

  const allAppointmentTypes: Array<{
    label: string;
    cycle: ScheduleExpectationCycle | null | undefined;
  }> = [
    { label: 'Psychiatry Check-in', cycle: psychiatryCycle },
    { label: 'Medical Check-in', cycle: medicalCycle },
    { label: 'Dual Diagnosis Psychiatry Follow-up', cycle: dualDiagnosisCycle }
  ];

  const appointmentTypesWithScheduledCycles = allAppointmentTypes.filter(
    (entry): entry is CycleEntryWithData => !!entry.cycle
  );

  const hasScheduledCycles = appointmentTypesWithScheduledCycles.length > 0;

  if (!hasScheduledCycles) {
    const allLabels = allAppointmentTypes.map((entry) => entry.label);
    return (
      <div className="mb-4" data-testid="next-scheduled-appointment-alert">
        <Alert level="info" dismissable={false}>
          <div className="text-sm font-medium text-blue-800">
            No appointment is scheduled nor currently expected to be scheduled for the following
            appointment types:
            <ul className="list-disc pl-4 mt-1">
              {allLabels.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="mb-4" data-testid="next-scheduled-appointment-alert">
      <Alert level="warn" dismissable={false}>
        <div className="text-sm font-medium text-blue-800">
          <p className="font-semibold mb-1">Next Scheduled Appointments</p>
          <ul className="list-disc pl-4 space-y-2">
            {appointmentTypesWithScheduledCycles.map(({ label, cycle }) => {
              const nextAppointment = cycle.appointment_attendances[0];
              return (
                <li key={label}>
                  <span className="font-semibold">{label}:</span>{' '}
                  {nextAppointment
                    ? `A follow-up is already scheduled for ${formatMonthDayOrToday(nextAppointment.start_at)} (${daysUntilMin0(nextAppointment.start_at)} days out). Create this task only if the patient needs to be seen sooner or if this timing needs to change.`
                    : `Scheduling will arrange a follow-up between ${formatMonthDayOrToday(cycle.cycle_start_at)} and ${formatMonthDayOrToday(cycle.cycle_end_at)} (${daysUntilMin0(cycle.cycle_start_at)}–${daysUntilMin0(cycle.cycle_end_at)} days out). Create this task if the patient needs meds sooner than ${daysUntilMin0(cycle.cycle_end_at)} days or if this range doesn't work for any other reason`}
                </li>
              );
            })}
          </ul>
        </div>
      </Alert>
    </div>
  );
};

export default NextScheduledAppointmentAlert;
